<script lang="ts">
	/**
	 * A small component that renders a text with an icon. Usuallly used inside of a Link
	 * or Directus Link. The icon and text can either be provided directly with the icon and display_text
	 * field or global images and texts can be used with the iconKey and textKey fields.
	 * The component is designed that you can give it the link field form directus using spread props.
	 *
	 * Example usages:
	 *
	 * <TextWithIcon {...article.link} iconPos="before" />
	 * <TextWithIcon icon={{id: 'a133-f4b6-...', type: 'image/jpg', media_description="beautiful image"}} display_text="My Text" />
	 * <TextWithIcon iconKey="my_icon" textKey="my_text" iconPos="before" />
	 */
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import Media from '$lib/components/media/Media.svelte';
	import Text from '$lib/components/Text.svelte';

	interface Icon {
		id: string;
		type: string;
		media_description?: string;
	}

	/*********
	 * Props *
	 **********/
	export let iconKey: string | null = null; // If not used with a directus link, you can specify a global media key here for the icon
	export let icon: Icon | null = null; // The icon object, usually from directus
	export let iconPos: 'before' | 'after' = 'after'; // Position the icon before or after the text
	export let iconHeight: string = '16px';
	export let iconWidth: string = '16px';
	export let color: 'black' | 'white' | 'yellow' = 'black';

	export let textKey: string = ''; // (Optional) text key of a global text to display
	export let display_text: string = ''; // The text to display
</script>

<span class="text-with-icon color-{color}">
	{#if iconPos === 'before' && (iconKey || icon)}
		<span class="icon before" style="--icon-height: {iconHeight}; --icon-width: {iconWidth}">
			{#if iconKey}
				<GlobalMedia key={iconKey} />
			{:else if icon?.id && icon?.type}
				<Media id={icon.id} type={icon.type} media_description={icon.media_description} />
			{/if}
		</span>
	{/if}
	<slot name="custom_before" />
	{#if textKey}
		<Text key={textKey} />
	{:else}
		{display_text}
	{/if}
	<slot name="custom_after" />
	{#if iconPos === 'after' && (iconKey || icon)}
		<span class="icon after" style="--icon-height: {iconHeight}; --icon-width: {iconWidth}">
			{#if iconKey}
				<GlobalMedia key={iconKey} />
			{:else if icon?.id && icon?.type}
				<Media id={icon.id} type={icon.type} media_description={icon.media_description} />
			{/if}
		</span>
	{/if}
</span>

<style lang="scss">
	.text-with-icon {
		display: inline-flex;
		align-items: center;
		text-decoration: none;
		@include font(h6);
		transition: all 0.3s ease;

		border-bottom: 2px solid transparent;

		&.color-black {
			color: color(black);

			.icon :global(svg path) {
				fill: color(black);
				stroke: color(black);
			}
		}

		&.color-white {
			color: color(white);

			.icon :global(svg path) {
				fill: color(white);
				stroke: color(white);
			}
		}

		&.color-yellow {
			color: color(yellow);

			.icon :global(svg path) {
				fill: color(yellow);
				stroke: color(yellow);
			}
		}

		&.color-black:hover {
			border-bottom: 2px solid color(black);
		}
		&.color-white:hover {
			border-bottom: 2px solid color(white);
		}
		&.color-yellow:hover {
			border-bottom: 2px solid color(yellow);
		}
	}

	.icon {
		height: var(--icon-height);
		width: var(--icon-width);
		overflow: hidden;
		margin-bottom: 3px;

		&.before {
			margin-inline-end: 8px;
		}
		&.after {
			margin-inline-start: 8px;
		}
	}
</style>
